import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import GuestEditModal from "../Components/Common/Model/GuestEditModal";
import PaymentLoadingModal from "../Components/Common/Model/PaymentLoadingModal";
import ShowdatesModal from "../Components/Common/Model/ShowdatesModal";
import { fetchCmsMetaData } from "../Components/Utility/cmsMetaService";
import CouponUpdatedModal from "../Components/Utility/CouponUpdatedModal";
import { SetMetaTags } from "../Components/Utility/setMetaTags";
import LoginModal from "./Users/LoginModal";
import OtherInfo from "./Users/OtherInfo";

// Stripe init
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function Checkout() {
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const slotId = queryParams.get("slot");
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const [showOtherInfoModal, setShowOtherInfoModal] = useState(false);
  const storedUser = localStorage.getItem("user");

  const [experienceData, setExperienceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingId, setBookingId] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);

  // ✅ State for guests
  // const [guests, setGuests] = useState(1);
  const [guests, setGuests] = useState(location.state?.guests || 1);

  const [totalPrice, setTotalPrice] = useState(location.state?.totalPrice || 1);
  const [packageId, setPackageId] = useState(location.state?.packageId || 1);
  useEffect(() => {
    const fetchExperienceDetails = async (currencyId) => {
      try {
        const res = await axios.get(
          `${
            process.env.REACT_APP_API_BASE
          }/getHomeExperienceDetails/${id}?&currency_id=${
            currencyId || "6"
          }&package_id=${packageId}`,
        );

        setExperienceData(res.data);
      } catch (err) {
        console.error("Error fetching experience details:", err);
      } finally {
        setLoading(false);
      }
    };

    // 🔹 Get initial currency from localStorage or set default
    const getCurrencyId = () => {
      const selectedCurrency = JSON.parse(
        localStorage.getItem("selectedCurrency"),
      );
      return selectedCurrency?.id || ""; // using `.id` since your object structure is { id, symbol, ... }
    };

    let currentCurrencyId = getCurrencyId();
    fetchExperienceDetails(currentCurrencyId);

    // 🔹 Listen to changes in localStorage (when updated from any tab)
    const handleStorageChange = (e) => {
      if (e.key === "selectedCurrency") {
        const updatedCurrency = JSON.parse(e.newValue);
        const newCurrencyId = updatedCurrency?.id || "";
        if (newCurrencyId !== currentCurrencyId) {
          currentCurrencyId = newCurrencyId;
          fetchExperienceDetails(newCurrencyId);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // 🔹 Detect same-tab changes (when you setItem manually)
    const localCheck = setInterval(() => {
      const local = getCurrencyId();
      if (local !== currentCurrencyId) {
        currentCurrencyId = local;
        fetchExperienceDetails(local);
      }
    }, 1200); // check every 1.2s

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(localCheck);
    };
  }, [id]);

  const allSlots = Object.values(experienceData?.data?.slots || {}).flat();

  // ✅ Find the slot by ID
  const currentSlot = allSlots.find((s) => String(s.id) === slotId);

  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm
        id={id}
        token={token}
        currentSlot={currentSlot}
        experienceData={experienceData}
        navigate={navigate}
        setLoginOpen={setLoginOpen}
        loginOpen={loginOpen}
      />
    </Elements>
  );
}

// Extracted form logic into a separate component so it can use `useStripe` and `useElements`
function CheckoutForm({
  id,
  token,
  currentSlot,
  experienceData,
  navigate,
  setLoginOpen,
  loginOpen,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const location = useLocation();
  const [country, setCountry] = useState("AU");
  const [pincode, setPincode] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showOtherInfoModal, setShowOtherInfoModal] = useState(false);
  const storedUser = localStorage.getItem("user");
  const [meta, setMeta] = useState();

  // Coupan Variable
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [couponLoader, setCouponLoader] = useState(false);

  const [coupons, setCoupons] = useState([]);
  const [appliedCouponsData, setAppliedCouponsData] = useState([]);

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE}/user/coupons`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (res.data?.status) {
          setCoupons(res.data.data);
        }
      } catch (err) {
        console.error("Coupon fetch error:", err);
      }
    };

    if (token) fetchCoupons();
  }, [token]);

  const handleApplyCoupon = async () => {
    if (!couponCode) {
      toast.error("Please enter coupon code");
      return;
    }

    try {
      setCouponLoader(true);
      const user = JSON.parse(localStorage.getItem("user"));

      const total =
        (guests.adults || 0) * Number(experienceData?.data?.price || 0) +
        (guests.children || 0) *
          Number(experienceData?.data?.child_price || 0) +
        (guests.infants || 0) * Number(experienceData?.data?.infant_price || 0);

      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/user/apply-coupon`,
        {
          coupon_code: couponCode,
          cart_total: total,
          user_id: user?.id,
          experience_id: experienceData?.data?.experience_id,
          currency_id: currencyId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (res.data?.status) {
        // console.log("Applied coupon response:", res.data);
        // const found = coupons.find(
        //   (c) => c.coupon_code === res.data.data.coupon_code,
        // );
        const couponData = res.data.data;

        // store applied coupon properly
        setAppliedCoupon({
          id: couponData.coupon_id,
          coupon_code: couponData.coupon_code,
          name: couponData.name,
          offer_title: couponData.offer_title,
          min_spend: couponData.min_spend,
          max_discount_cap: couponData.max_discount_cap,
          discount_value: couponData.discount_value,
          discount_type: couponData.discount_type,
        });

        // console.log("Matching coupon from list:", couponData);

        // setAppliedCoupon(found || { coupon_code: res.data.data.coupon_code });
        setAppliedCouponsData(res.data.data);
        setDiscountAmount(res.data.data.discount);

        toast.success(res.data.message);
      }
      // ❗ show backend error
      else {
        toast.error(res.data?.message || "Coupon not valid");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to apply coupon");
    } finally {
      setCouponLoader(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscountAmount(0);
    setCouponCode("");
  };

  const closeOtherInfo = () => {
    setShowOtherInfoModal(false);
  };

  // const [guests, setGuests] = useState(1);
  const [guests, setGuests] = useState(location.state?.guests || 1);

  const [currencyId, setCurrencyId] = useState(
    location.state?.currencyId || "$",
  );
  const [currency, setCurrency] = useState(location.state?.currency || "AUD");
  const [currencysymbol, setcurrencysymbol] = useState(
    location.state?.currency_symbol || "AUD",
  );
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [isPaying, setIsPaying] = useState(false);
  // ✅ Maintain slot date and details in state
  const [slotDate, setSlotDate] = useState(location.state?.slotDate || "");
  const [slotDetails, setSlotDetails] = useState(
    location.state?.slotDetails || null,
  );

  const [guestModalOpen, setGuestModalOpen] = useState(false);

  const [tempGuests, setTempGuests] = useState(guests); // for editing
  const [showGuestEditor, setShowGuestEditor] = useState(false);

  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [packageId, setPackageId] = useState(location.state?.packageId || 1);

  const [countries, setCountries] = useState([]);

  const [participantCount, setParticipantCount] = useState({
    adults: location.state?.guests?.adults ?? 1,
    children: location.state?.guests?.children ?? 0,
    infants: location.state?.guests?.infants ?? 0,
  });

  const [showCouponUpdatedModal, setShowCouponUpdatedModal] = useState(false);
  const [couponUpdateMessage, setCouponUpdateMessage] = useState("");

  // const handleCountChange = (type, action) => {
  //   setParticipantCount((prev) => {
  //     let updated = { ...prev };

  //     const maxAdult = Number(experienceData?.package?.no_of_adult || 0);
  //     const maxChild = Number(experienceData?.package?.no_of_childs || 0);
  //     const maxInfant = Number(experienceData?.package?.no_of_infant || 0);

  //     // Decide total limit priority
  //     let totalLimit = 0;
  //     if (maxAdult > 0) totalLimit = maxAdult;
  //     else if (maxChild > 0) totalLimit = maxChild;
  //     else totalLimit = maxInfant;

  //     const totalAll = prev.adults + prev.children + prev.infants;

  //     // Adults
  //     if (type === "adults") {
  //       if (action === "increment" && totalAll < totalLimit) updated.adults += 1;
  //       else if (action === "decrement" && prev.adults > 1) updated.adults -= 1;
  //     }

  //     // Children
  //     if (type === "children") {
  //       if (action === "increment" && totalAll < totalLimit)
  //         updated.children += 1;
  //       else if (action === "decrement" && prev.children > 0)
  //         updated.children -= 1;
  //     }

  //     // Infants
  //     if (type === "infants") {
  //       if (action === "increment" && totalAll < totalLimit)
  //         updated.infants += 1;
  //       else if (action === "decrement" && prev.infants > 0)
  //         updated.infants -= 1;
  //     }

  //     return updated;
  //   });
  // };

  // const handleSaveGuests = () => {
  //   // setGuests(tempGuests); // update main
  //   // setGuests({ adults, children, infants });
  //   setShowGuestEditor(false); // close editor
  // };

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASE}/getCountries`)
      .then((res) => setCountries(res.data.data || []))
      .catch(() => setCountries([]));
  }, []);

  const handleCountChange = (type, action) => {
    setParticipantCount((prev) => {
      let updated = { ...prev };

      const maxAdult = Number(experienceData?.package?.no_of_adult || 0);
      const maxChild = Number(experienceData?.package?.no_of_childs || 0);
      const maxInfant = Number(experienceData?.package?.no_of_infant || 0);

      //  Slot availability limit
      // const availableLimit = Number(currentSlot?.available_pax || 0);
      const availableLimit = Number(
        slotDetails?.available_pax || currentSlot?.available_pax || 0,
      );

      const totalSelected = prev.adults + prev.children + prev.infants;

      // Adults
      if (type === "adults") {
        if (action === "increment") {
          if (totalSelected < availableLimit && prev.adults < maxAdult) {
            updated.adults += 1;
          }
        } else if (action === "decrement" && prev.adults > 1) {
          updated.adults -= 1;
        }
      }

      // Children
      if (type === "children") {
        if (action === "increment") {
          if (totalSelected < availableLimit && prev.children < maxChild) {
            updated.children += 1;
          }
        } else if (action === "decrement" && prev.children > 0) {
          updated.children -= 1;
        }
      }

      // Infants
      if (type === "infants") {
        if (action === "increment") {
          if (totalSelected < availableLimit && prev.infants < maxInfant) {
            updated.infants += 1;
          }
        } else if (action === "decrement" && prev.infants > 0) {
          updated.infants -= 1;
        }
      }

      return updated;
    });
  };

  const handleSaveGuests = () => {
    setGuests(participantCount);
    // reset coupon
    setAppliedCoupon(null);
    setDiscountAmount(0);
    setCouponCode("");

    setShowGuestEditor(false);
  };

  const handleEditDateClick = () => {
    setIsDateModalOpen(true);
  };

  const handleCancelGuests = () => {
    setTempGuests(guests); // revert changes
    setShowGuestEditor(false);
  };

  const handleGuestSave = (updatedGuests) => {
    setGuests(updatedGuests);
  };

  
  const handleConfirmPay = async (e) => {
    e.preventDefault();

    let bookingPriceTemp = {
      total_price: 0,
      currency: "",
      discount: 0,
      coupon_code: "",
    };

    try {
      if (!token) {
        setLoginOpen(true);
        return;
      }
      setIsPaying(true);

      const user = JSON.parse(localStorage.getItem("user"));
      if (!user?.id) {
        toast.error("User information missing, please log in again.");
        setLoginOpen(true);
        return;
      }

      // Stripe & Elements check
      if (!stripe || !elements) {
        toast.error("Stripe has not loaded yet");
        return;
      }

      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) {
        toast.error("Card input not ready");
        return;
      }

      if (!pincode || !country) {
        toast.error("Please enter your pincode and select your country");
        return;
      }
      if (!pincode || pincode.length < 4) {
        toast.error("Please enter a valid pincode");
        return false;
      }

      if (!termsAccepted) {
        toast.error(
          "Please accept the terms and conditions before proceeding.",
        );
        return;
      }

      const userAddress = {
        address: "Sector 62, Noida",
        pincode: pincode,
        contry_code: country,
      };
      const bookingDate = slotDate || new Date().toISOString().split("T")[0];

      const bookingFromTime = slotDetails?.from_time || currentSlot?.from_time;
      const bookingToTime = slotDetails?.to_time || currentSlot?.to_time;
      const totalGuests =
        (guests?.adults || 0) +
        (guests?.children || 0) +
        (guests?.infants || 0);

      const formatGuests = () => {
        const parts = [];

        if (guests?.adults > 0) {
          parts.push(`${guests.adults} adult${guests.adults > 1 ? "s" : ""}`);
        }

        if (guests?.children > 0) {
          parts.push(
            `${guests.children} child${guests.children > 1 ? "ren" : ""}`,
          );
        }

        if (guests?.infants > 0) {
          parts.push(
            `${guests.infants} infant${guests.infants > 1 ? "s" : ""}`,
          );
        }

        return parts.join(", ");
      };

      // 1. Pre-booking API
      // console.log("Pre-booking with:", appliedCoupon?.id);
      const preRes = await axios.post(
        `${process.env.REACT_APP_API_BASE}/user/pre-booking`,
        {
          host_id: experienceData?.data?.host_id,
          experience_id: experienceData?.data?.experience_id,
          package_id: packageId,
          booking_from_time: bookingFromTime,
          booking_date: bookingDate,
          booking_to_time: bookingToTime,
          currency_id: currencyId || 6,
          currency: currency || "AUD",
          // guest_count: guests,
          guest_count:
            // totalGuests,
            guests?.adults ? Number(guests.adults) : 0,
          children_count: guests?.children ? Number(guests.children) : 0,
          infants_count: guests?.infants ? Number(guests.infants) : 0,
          coupon_id: appliedCoupon?.id || null,
          old_min_spend: appliedCoupon?.min_spend || null,
          old_max_discount_cap: appliedCoupon?.max_discount_cap || null,
          old_discount_value: appliedCoupon?.discount_value || null,
          old_discount_type: appliedCoupon?.discount_type || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const newBookingId = preRes.data?.booking_id;

      // 2. Create PaymentIntent
      const paymentRes = await axios.post(
        `${process.env.REACT_APP_API_BASE}/user/create-payment-intent`,
        {
          booking_id: newBookingId,
          currency: currency || "AUD",
          address: userAddress.address,
          pincode: userAddress.pincode,
          contry_code: userAddress.contry_code,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const { clientSecret } = paymentRes.data;

      const checkBookingStatusTemp = async (newBookingId, token) => {
        let attempts = 0;
        const maxAttempts = 4; // 15s polling

        while (attempts < maxAttempts) {
          try {
            const res = await axios.post(
              `${process.env.REACT_APP_API_BASE}/user/get-booking-by-id`,
              { booking_id: newBookingId },
              { headers: { Authorization: `Bearer ${token}` } },
            );

            if (res.data?.status === true) {
              bookingPriceTemp.total_price = res.data.data.total_price;
              bookingPriceTemp.currency = res.data.data.currency;
              bookingPriceTemp.discount = res.data.data.discount;
              bookingPriceTemp.coupon_code = res.data.data.coupon_code;
              return true;
            }
          } catch (err) {
            console.warn(
              "Checking booking status attempt:",
              attempts + 1,
              err.message,
            );
          }

          await new Promise((r) => setTimeout(r, 4000)); // wait 1s
          attempts++;
        }

        return false;
      };

      // 3. Confirm Payment
      const { error: confirmError, paymentIntent } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: "Customer Name",
              address: {
                postal_code: pincode,
                country: country,
              },
            },
          },
        });

      if (confirmError) {
        console.error("Payment confirmation error:", confirmError);
        alert(confirmError.message || "Payment failed!");
        return;
      }
      let finalPaymentIntent = paymentIntent;
      if (paymentIntent.status === "requires_action") {
        const { error: actionError, paymentIntent: actionPaymentIntent } =
          await stripe.handleCardAction(paymentIntent.client_secret);

        if (actionError) {
          toast.error("3D Secure failed!");
          return;
        }

        // Confirm again if needed
        finalPaymentIntent = actionPaymentIntent;
      }

      const adultTotal =
        (guests.adults || 0) * Number(experienceData?.data?.price || 0);

      const childTotal =
        (guests.children || 0) * Number(experienceData?.data?.child_price || 0);

      const infantTotal =
        (guests.infants || 0) * Number(experienceData?.data?.infant_price || 0);

      const bookingVerified = await checkBookingStatusTemp(newBookingId, token);

      if (bookingVerified) {
        navigate("/thankyou", {
          state: {
            experienceId: experienceData?.data?.experience_id,
            bookingId: newBookingId,
            transactionId: paymentIntent.id,
            transactionMode: paymentIntent.payment_method_types[0],
            experienceName: experienceData?.data?.experience_title,
            experienceImage: `${experienceData?.base_url}${experienceData?.data?.images?.[0]?.image}`,
            rating: experienceData?.data?.rating,
            reviews: experienceData?.data?.reviews_count,
            slug: experienceData?.data?.url_slug,
            // date: `${currentSlot?.slot_date} | ${currentSlot?.from_time} - ${currentSlot?.to_time}`,
            date:
              slotDate && slotDetails
                ? `${slotDate} | ${slotDetails.from_time} - ${slotDetails.to_time}`
                : currentSlot
                  ? `${currentSlot.slot_date} | ${currentSlot.from_time} - ${currentSlot.to_time}`
                  : "Date not available",

            guests: formatGuests(),
            price: `${currencysymbol || "$"} ${Number(adultTotal + childTotal + infantTotal).toFixed(2)} ${bookingPriceTemp.currency}`,
            discount: `${currencysymbol || "$"} ${Number(bookingPriceTemp.discount).toFixed(2)} ${bookingPriceTemp.currency}`,
            coupon_code: bookingPriceTemp.coupon_code,
            // total: `${currencysymbol || "$"} ${(
            //   (guests.adults || 0) * Number(experienceData?.data?.price) +
            //   (guests.children || 0) *
            //     Number(experienceData?.data?.child_price) +
            //   (guests.infants || 0) * Number(experienceData?.data?.infant_price)
            // ).toFixed(2)} ${currency || "AUD"}`,
            total: `${currencysymbol || "$"} ${Number(bookingPriceTemp.total_price).toFixed(2)} ${bookingPriceTemp.currency}`,
          },
        });

        sessionStorage.setItem(
          "thankyouBooking",
          JSON.stringify({
            experienceId: experienceData?.data?.experience_id,
            bookingId: newBookingId,
            transactionId: paymentIntent.id,
            transactionMode: paymentIntent.payment_method_types[0],
            experienceName: experienceData?.data?.experience_title,
            experienceImage: `${experienceData?.base_url}${experienceData?.data?.images?.[0]?.image}`,
            rating: experienceData?.data?.rating,
            reviews: experienceData?.data?.reviews_count,
            slug: experienceData?.data?.url_slug,
            date:
              slotDate && slotDetails
                ? `${slotDate} | ${slotDetails.from_time} - ${slotDetails.to_time}`
                : currentSlot
                  ? `${currentSlot.slot_date} | ${currentSlot.from_time} - ${currentSlot.to_time}`
                  : "Date not available",
            guests: formatGuests(),
            price: `${currencysymbol || "$"} ${Number(adultTotal + childTotal + infantTotal).toFixed(2)} ${bookingPriceTemp.currency}`,
            discount: `${currencysymbol || "$"} ${Number(bookingPriceTemp.discount).toFixed(2)} ${bookingPriceTemp.currency}`,
            coupon_code: bookingPriceTemp.coupon_code,
            // total: `${currencysymbol || "$"} ${(
            //   (guests.adults || 0) * Number(experienceData?.data?.price) +
            //   (guests.children || 0) *
            //     Number(experienceData?.data?.child_price) +
            //   (guests.infants || 0) * Number(experienceData?.data?.infant_price)
            // ).toFixed(2)} ${currency || "AUD"}`,
            total: `${currencysymbol || "$"} ${Number(bookingPriceTemp.total_price).toFixed(2)} ${bookingPriceTemp.currency}`,
          }),
        );
      } else {
        const refreshedIntent = await stripe.retrievePaymentIntent(
          finalPaymentIntent.client_secret,
        );

        if (refreshedIntent.paymentIntent.status === "succeeded") {
          // ✅ Payment succeeded without 3D Secure

          const finalRes = await axios.post(
            `${process.env.REACT_APP_API_BASE}/user/final-booking`,
            {
              booking_id: newBookingId,
              transaction_id: paymentIntent.id,
            },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          // ✅ set price from final-booking API
          bookingPriceTemp.total_price = finalRes.data.data.total_price;
          bookingPriceTemp.currency = finalRes.data.data.currency;
          navigate("/thankyou", {
            state: {
              experienceId: experienceData?.data?.experience_id,
              bookingId: newBookingId,
              transactionId: paymentIntent.id,
              transactionMode: paymentIntent.payment_method_types[0],
              experienceName: experienceData?.data?.experience_title,
              experienceImage: `${experienceData?.base_url}${experienceData?.data?.images?.[0]?.image}`,
              rating: experienceData?.data?.rating,
              slug: experienceData?.data?.url_slug,
              reviews: experienceData?.data?.reviews_count,
              slug: experienceData?.data?.url_slug,
              // date: `${currentSlot?.slot_date} | ${currentSlot?.from_time} - ${currentSlot?.to_time}`,
              date:
                slotDate && slotDetails
                  ? `${slotDate} | ${slotDetails.from_time} - ${slotDetails.to_time}`
                  : currentSlot
                    ? `${currentSlot.slot_date} | ${currentSlot.from_time} - ${currentSlot.to_time}`
                    : "Date not available",

              guests: formatGuests(),
              price: `${currencysymbol || "$"} ${Number(adultTotal + childTotal + infantTotal).toFixed(2)} ${bookingPriceTemp.currency}`,
              discount: `${currencysymbol || "$"} ${Number(bookingPriceTemp.discount).toFixed(2)} ${bookingPriceTemp.currency}`,
              coupon_code: bookingPriceTemp.coupon_code,
              // total: `${currencysymbol || "$"} ${(
              //   (guests.adults || 0) * Number(experienceData?.data?.price) +
              //   (guests.children || 0) *
              //     Number(experienceData?.data?.child_price) +
              //   (guests.infants || 0) *
              //     Number(experienceData?.data?.infant_price)
              // ).toFixed(2)} ${currency || "AUD"}`,
              total: `${currencysymbol || "$"} ${Number(
                bookingPriceTemp.total_price,
              ).toFixed(2)} ${bookingPriceTemp.currency}`,
            },
          });

          sessionStorage.setItem(
            "thankyouBooking",
            JSON.stringify({
              experienceId: experienceData?.data?.experience_id,
              bookingId: newBookingId,
              transactionId: paymentIntent.id,
              transactionMode: paymentIntent.payment_method_types[0],
              experienceName: experienceData?.data?.experience_title,
              experienceImage: `${experienceData?.base_url}${experienceData?.data?.images?.[0]?.image}`,
              rating: experienceData?.data?.rating,
              reviews: experienceData?.data?.reviews_count,
              slug: experienceData?.data?.url_slug,
              date:
                slotDate && slotDetails
                  ? `${slotDate} | ${slotDetails.from_time} - ${slotDetails.to_time}`
                  : currentSlot
                    ? `${currentSlot.slot_date} | ${currentSlot.from_time} - ${currentSlot.to_time}`
                    : "Date not available",
              guests: formatGuests(),
              price: `${currencysymbol || "$"} ${Number(adultTotal + childTotal + infantTotal).toFixed(2)} ${bookingPriceTemp.currency}`,
              discount: `${currencysymbol || "$"} ${Number(bookingPriceTemp.discount).toFixed(2)} ${bookingPriceTemp.currency}`,
              coupon_code: bookingPriceTemp.coupon_code,
              // total: `${currencysymbol || "$"} ${(
              //   (guests.adults || 0) * Number(experienceData?.data?.price) +
              //   (guests.children || 0) *
              //     Number(experienceData?.data?.child_price) +
              //   (guests.infants || 0) *
              //     Number(experienceData?.data?.infant_price)
              // ).toFixed(2)} ${currency || "AUD"}`,
              total: `${currencysymbol || "$"} ${Number(
                bookingPriceTemp.total_price,
              ).toFixed(2)} ${bookingPriceTemp.currency}`,
            }),
          );
        } else {
          const status = refreshedIntent.paymentIntent.status;
          let userMessage = "";

          switch (status) {
            case "processing":
              userMessage =
                "Your payment is still processing. We’ll confirm your booking automatically once it’s completed.";
              break;
            case "requires_payment_method":
              userMessage =
                "Your payment could not be processed. Please check your card details or try another payment method.";
              break;
            case "requires_confirmation":
              userMessage =
                "Your payment is pending confirmation. Please wait a few moments while we verify it.";
              break;
            default:
              userMessage =
                "It’s taking longer than usual to confirm your payment. We’ll notify you once your booking is confirmed.";
          }

          toast.error(userMessage);
        }
      }
    } catch (error) {
      console.error("Booking error:", error);
      if (
        error.response?.status === 401 ||
        error.response?.data?.message === "Unauthenticated."
      ) {
        localStorage.removeItem("authToken");
        setLoginOpen(true);
        return;
      }
      if (
        error.response?.data?.message === "Host can not book your experience."
      ) {
        toast.error("Host can not book your experience.");
        return;
      } // 3️⃣ User profile not completed
      if (
        error.response?.data?.message ===
        "Please complete your profile before making a booking."
      ) {
        //  toast.error(error.response?.data?.message);
        setTimeout(() => {
          setShowOtherInfoModal(true);
          // <-- Use your modal state function here
        }, 300);
        return;
      }

      if (
        error.response?.data?.message ===
        "Your applied coupon code has been updated."
      ) {
        setCouponUpdateMessage(error.response.data.message);
        setShowCouponUpdatedModal(true);

        setAppliedCoupon(null);
        setDiscountAmount(0);
        setCouponCode("");

        return;
      }
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong, please try again.",
      );
    } finally {
      setIsPaying(false); // reset loading
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const getCancellationDate = () => {
    const slot = currentSlot || slotDetails;
    if (!slot) return "";

    const duration = Number(experienceData?.data?.cancellation_duration || 0);

    // Extract parts safely
    const [year, month, day] = slotDate.split("-").map(Number);
    const [hour, minute, second] = slotDetails?.from_time
      .split(":")
      .map(Number);

    // Create date in LOCAL TIME (no timezone shift)
    const slotDateTime = new Date(year, month - 1, day, hour, minute, second);

    // Subtract cancellation days
    slotDateTime.setDate(slotDateTime.getDate() - duration);

    return slotDateTime.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Converts "HH:mm" or full ISOF date to "hh:mm AM/PM"
  const formatTimeAMPM = (timeString) => {
    if (!timeString) return "";

    const [hours, minutes] = timeString.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return timeString;

    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 === 0 ? 12 : hours % 12;
    return `${formattedHours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")} ${ampm}`;
  };

  // Inside CheckoutForm component
  const handleDateSelect = (newDate, newSlot) => {
    setSlotDate(newDate);
    setSlotDetails(newSlot);
    setParticipantCount({ adults: 1, children: 0, infants: 0 });
    setGuests({ adults: 1, children: 0, infants: 0 });

    // reset coupon
    setAppliedCoupon(null);
    setDiscountAmount(0);
    setCouponCode("");

    setIsDateModalOpen(false);
  };

  const getExperienceUrl = (url_slug) => {
    return `/experience/${url_slug}`;
  };

  useEffect(() => {
    const loadMeta = async () => {
      const data = await fetchCmsMetaData("checkout");
      setMeta(data);
    };

    loadMeta();
  }, []);

  useEffect(() => {
    if (appliedCoupon) {
      setAppliedCoupon(null);
      setDiscountAmount(0);
      setCouponCode("");
    }
  }, [currencyId]);

  return (
    <>
      {/* <!-- Confirm and pay --> */}
      <section class="ser_v_araeea detailspgess checkout">
        <SetMetaTags
          title={meta?.meta_title || "Checkout | Weyfarin"}
          description={meta?.meta_description || "Checkout | Weyfarin"}
        />

        <div class="container">
          <div class="top_bk_areaa">
            <Link className="ar_left" onClick={() => navigate(-1)}>
              <img src="/images/chevron-left.png" alt="Back" />
            </Link>{" "}
            Confirm and pay
          </div>
          <div class="row">
            <div class="col-lg-6">
              <div class="chek_bx_txt">
                <div class="meddils">
                  <div
                    class="mg_arease"
                    onClick={() =>
                      navigate(
                        getExperienceUrl(
                         experienceData?.data?.url_slug
                        ),
                      )
                    }
                  >
                    <img
                      src={`${experienceData?.base_url}${experienceData?.data?.images?.[0]?.image}`}
                      alt={experienceData?.data?.title}
                    />
                  </div>
                  <div class="dtls_rightss  tetx_arease">
                    <h3
                      onClick={() =>
                        navigate(
                          getExperienceUrl(
                                experienceData?.data?.url_slug
                          ),
                        )
                      }
                    >
                      {experienceData?.data?.experience_title}
                    </h3>
                    <div class="us_listst">
                      <ul>
                        <li>
                          <i className="fa fa-star"></i>{" "}
                          {experienceData?.data?.rating || "0"}/5{" "}
                        </li>
                        <li
                          className={
                            experienceData?.data?.reviews_count > 0
                              ? ""
                              : "platforma_cursor"
                          }
                          onClick={() => {
                            if (experienceData?.data?.reviews_count > 0) {
                              navigate(
                                getExperienceUrl(
                                  experienceData?.data?.url_slug
                                ),
                              );
                            }
                          }}
                        >
                          {experienceData?.data?.reviews_count} reviews
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* <div class="col-lg-6">
              <div class="ck_book_fr">
                <div class="tx_lfters">
                  <h3>Book for just your group</h3>
                  <p>Make it private: $24.94 AUD more</p>
                  <span>
                    Must meet the host’s $95.35 AUD minimum before taxes or
                    discounts
                  </span>
                </div>

                <div class="ck_boxxx">
                  <div class="bothsredoibtn">
                    <label class="label--checkbox rft">
                      <input type="checkbox" class="checkbox" name="foo" />
                    </label>
                  </div>
                </div>
              </div>
            </div> */}
          </div>

          <div class="row">
            <div class="col-lg-6 col-sm-6">
              <div class="ck_ckalsinss">
                {experienceData?.data?.exp_cancel_status !== 2 && (
                  <div class="clk_liststs">
                    <h4>Free cancellation</h4>
                    <p>
                      Cancel before {getCancellationDate()} for a full refund.
                    </p>
                  </div>
                )}

                <div class="clk_liststs">
                  <h4>Date & Time Slot</h4>

                  <p>
                    {slotDate && slotDetails
                      ? `${slotDate} | ${formatTimeAMPM(
                          slotDetails.from_time,
                        )} - ${formatTimeAMPM(slotDetails.to_time)}`
                      : currentSlot
                        ? `${currentSlot.slot_date} | ${formatTimeAMPM(
                            currentSlot.from_time,
                          )} - ${formatTimeAMPM(currentSlot.to_time)}`
                        : "No slot selected"}
                  </p>

                  <button
                    type="button"
                    className="ed_links"
                    onClick={handleEditDateClick}
                  >
                    Edit
                  </button>
                </div>

                <div className="clk_liststs">
                  <h4>Guests</h4>

                  <p>
                    {guests.adults > 0 && (
                      <>
                        {guests.adults} adult{guests.adults > 1 ? "s" : ""}
                        {guests.children > 0 || guests.infants > 0 ? ", " : ""}
                      </>
                    )}
                    {guests.children > 0 && (
                      <>
                        {guests.children} child
                        {guests.children > 1 ? "ren" : ""}
                        {guests.infants > 0 ? ", " : ""}
                      </>
                    )}
                    {guests.infants > 0 && (
                      <>
                        {guests.infants} infant{guests.infants > 1 ? "s" : ""}
                      </>
                    )}
                  </p>

                  <button
                    type="button"
                    className="ed_links"
                    onClick={() => {
                      setTempGuests(guests);
                      setShowGuestEditor(true);
                    }}
                  >
                    Edit
                  </button>

                  {/* Guest editor modal here (same as before) */}
                  {showGuestEditor && (
                    <div className="add_modalss all_modalss">
                      <div className="modal-container">
                        <h3>Edit Guests</h3>
                        <button
                          className="btn btn-secondary"
                          onClick={handleCancelGuests}
                        >
                          <img src="/images/cancel_c.png" alt="" />
                        </button>
                        <div className="main_area_sect">
                          <div className="us_cntens">
                            <p>Adults</p>
                            <span>Age 13+</span>
                          </div>
                          <div className="biths_adses">
                            <span
                              className="addsss"
                              onClick={() =>
                                handleCountChange("adults", "decrement")
                              }
                            >
                              -
                            </span>
                            <input
                              type="text"
                              value={participantCount.adults}
                              readOnly
                              className="form-control"
                            />
                            <span
                              className="addsss"
                              onClick={() =>
                                handleCountChange("adults", "increment")
                              }
                            >
                              +
                            </span>
                          </div>
                        </div>

                        <div className="main_area_sect">
                          <div className="us_cntens">
                            <p>Children</p>
                            <span>Age 4–12</span>
                          </div>
                          <div className="biths_adses">
                            <span
                              className="addsss"
                              onClick={() =>
                                handleCountChange("children", "decrement")
                              }
                            >
                              -
                            </span>
                            <input
                              type="text"
                              value={participantCount.children}
                              readOnly
                              className="form-control"
                            />
                            <span
                              className="addsss"
                              onClick={() =>
                                handleCountChange("children", "increment")
                              }
                            >
                              +
                            </span>
                          </div>
                        </div>

                        <div className="main_area_sect">
                          <div className="us_cntens">
                            <p>Infants</p>
                            <span>Under 2</span>
                          </div>
                          <div className="biths_adses">
                            <span
                              className="addsss"
                              onClick={() =>
                                handleCountChange("infants", "decrement")
                              }
                            >
                              -
                            </span>
                            <input
                              type="text"
                              value={participantCount.infants}
                              readOnly
                              className="form-control"
                            />
                            <span
                              className="addsss"
                              onClick={() =>
                                handleCountChange("infants", "increment")
                              }
                            >
                              +
                            </span>
                          </div>
                        </div>

                        <div className="modal-actions">
                          <button
                            className="btn btn-primary full_wdth"
                            onClick={handleSaveGuests}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="clk_liststs haveacode">
                  <h4 className="mb-3">Have a coupon code ?</h4>

                  {/* INPUT */}
                  {!appliedCoupon && (
                    <div className="input-group mb-3 mngbothsbtnns">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Coupon Code"
                        value={couponCode}
                        maxLength={30}
                        onChange={(e) => setCouponCode(e.target.value)}
                      />
                      <button
                        className="btn btn-dark px-4"
                        type="button"
                        onClick={handleApplyCoupon}
                      >
                        {couponLoader ? (
                          <>
                            <span>Processing...</span>
                          </>
                        ) : (
                          "Apply"
                        )}
                      </button>
                    </div>
                  )}

                  {/* SUCCESS BOX (LIKE SCREENSHOT) */}
                  {appliedCoupon && (
                    <div className="p-3 d-flex justify-content-between align-items-start priceritems">
                      <div className="coupon-success">
                        <div className="me-2 d-flex align-items-center justify-content-center coupon-success-tick">
                          <img src="/images/thikss.svg" alt="tik" />
                        </div>

                        <div className="cntxtdata">
                          <strong className="text-success">
                            {appliedCoupon.coupon_code}
                          </strong>
                          <p className="mb-0 small text-success">
                            {appliedCoupon.name}
                          </p>
                          <p className="mb-0 text-success">
                            {appliedCoupon?.offer_title}
                          </p>
                          <p className="mb-0 text-success fw-semibold">
                            You save {currencysymbol}
                            {discountAmount.toFixed(2)} {currency || "AUD"} !
                          </p>
                        </div>
                      </div>

                      <button
                        className="btn coupon-success-close"
                        onClick={handleRemoveCoupon}
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  {/* AVAILABLE COUPONS */}
                  {!appliedCoupon && coupons.length > 0 && (
                    <div className="p-3 box-radient">
                      <p className="mb-2">Available Coupons</p>

                      <div className="d-flex flex-wrap coupan_name_div">
                        {coupons.map((c) => (
                          <button
                            key={c.id}
                            f
                            type="button"
                            className="btn btn-light border rounded-pill px-3"
                            onClick={() =>
                              setCouponCode((c.coupon_code || "").toUpperCase())
                            }
                          >
                            {(c.coupon_code || "").toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="clk_liststs">
                  <h4>Price details</h4>

                  <p>
                    {/* Adults */}
                    {guests.adults > 0 && (
                      <>
                        {currencysymbol}
                        {Number(experienceData?.data?.price || 0).toFixed(
                          2,
                        )} × {guests.adults} adult
                        <br />
                      </>
                    )}

                    {/* Children */}
                    {guests.children > 0 && (
                      <>
                        {currencysymbol}
                        {Number(experienceData?.data?.child_price || 0).toFixed(
                          2,
                        )}{" "}
                        × {guests.children} child
                        <br />
                      </>
                    )}

                    {/* Infants */}
                    {guests.infants > 0 && (
                      <>
                        {currencysymbol}
                        {Number(
                          experienceData?.data?.infant_price || 0,
                        ).toFixed(2)}{" "}
                        × {guests.infants} infant
                        <br />
                      </>
                    )}

                    {/* Total */}
                    <span className="lst">
                      {currencysymbol}
                      {(
                        (guests.adults || 0) *
                          Number(experienceData?.data?.price || 0) +
                        (guests.children || 0) *
                          Number(experienceData?.data?.child_price || 0) +
                        (guests.infants || 0) *
                          Number(experienceData?.data?.infant_price || 0)
                      ).toFixed(2)}{" "}
                      {currency || "AUD"}
                    </span>
                  </p>

                  {appliedCoupon && (
                    <div className="d-flex justify-content-between text-success mt-2 coupon-discount-price">
                      <span>Discount ({appliedCoupon.coupon_code})</span>
                      <span>
                        - {currencysymbol}
                        {discountAmount.toFixed(2)} {currency || "AUD"}
                      </span>
                    </div>
                  )}
                </div>

                <div className="clk_liststs">
                  <h4>
                    Total{" "}
                    <span className="lst">
                      {currencysymbol}
                      {(
                        (guests.adults || 0) *
                          Number(experienceData?.data?.price) +
                        (guests.children || 0) *
                          Number(experienceData?.data?.child_price) +
                        (guests.infants || 0) *
                          Number(experienceData?.data?.infant_price) -
                        discountAmount
                      ).toFixed(2)}{" "}
                      {currency || "AUD"}
                    </span>
                  </h4>

                  {appliedCoupon && (
                    <div className="text-center coupons-grandtotal submmitss">
                      You're saving {currencysymbol}
                      {discountAmount.toFixed(2)} {currency || "AUD"}!
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div class="col-lg-6 col-sm-6">
              <form action="">
                <div class="pay_crd_area">
                  <div class="ck_book_fr paymetntts">
                    <div class="tx_lfters">
                      <h3>Payment Method</h3>

                      <div className="bothsredoibtn">
                        <p className="lft">
                          <img src="/images/wallets.png" alt="" /> Credit or
                          Debit Card
                        </p>
                        <label className="label--radio rft">
                          <input
                            type="radio"
                            className="radio"
                            name="payment"
                            value="card"
                            checked={paymentMethod === "card"}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                          />
                        </label>
                      </div>

                      <div class="row">
                        <div class="col-lg-12">
                          <div class="form-group">
                            <label>Card Number</label>
                            <CardNumberElement className="form-control" />
                          </div>
                        </div>
                        <div class="col-lg-6">
                          <div class="form-group">
                            <label>Expiry date</label>
                            <CardExpiryElement className="form-control" />
                          </div>
                        </div>
                        <div class="col-lg-6">
                          <div class="form-group">
                            <label>CVV</label>
                            <CardCvcElement className="form-control" />
                          </div>
                        </div>
                        <div class="col-lg-12">
                          <div class="form-group">
                            <label>Postcode</label>
                            <input
                              type="text"
                              class="form-control"
                              value={pincode}
                              onChange={(e) => setPincode(e.target.value)}
                              onKeyPress={(e) => {
                                if (!/[0-9]/.test(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              maxLength={6}
                              placeholder="Enter Pincode"
                              required
                            />
                          </div>
                        </div>

                        <div class="col-lg-12">
                          <div class="form-group">
                            <label>Country / Region</label>

                            <select
                              className="form-control"
                              value={country}
                              onChange={(e) => setCountry(e.target.value)}
                            >
                              <option value="">Select Country</option>
                              {countries.map((c) => (
                                <option key={c.id} value={c.iso2}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* <div class="bothsredoibtn manas">
                        <p class="lft">
                          <img
                            src="/images/logos_paypal.png"
                            class="paypal"
                            alt=""
                          />
                        </p>
                        <label class="label--radio rft">
                          <input type="radio" class="radio" name="redio" />
                        </label>
                      </div>

                      <div class="bothsredoibtn manas">
                        <p class="lft">
                          <img
                            src="/images/google_ic.png"
                            class="gopay"
                            alt=""
                          />
                        </p>
                        <label class="label--radio rft">
                          <input
                            type="radio"
                            className="radio gopay"
                            name="redio"
                          />
                        </label>
                      </div> */}
                    </div>
                  </div>

                  <div class="disspay">
                    <div class="bothsredoibtn">
                      <label class="label--checkbox rft">
                        <input
                          type="checkbox"
                          className="checkbox gopay"
                          name="terms"
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                        />
                      </label>
                    </div>
                    <div className="prv_liststs">
                      By selecting the button, I agree to the booking terms and
                      release and waiver. View{" "}
                      <Link
                        to={"/privacy-policy"}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </Link>
                      .
                    </div>
                  </div>
                  <div className="submmit">
                    <button
                      type="submit"
                      onClick={handleConfirmPay}
                      disabled={isPaying}
                    >
                      {isPaying ? (
                        <>
                          {/* <span className="spinner"></span>{" "} */}
                          <span>Processing...</span>
                        </>
                      ) : (
                        "Confirm and pay"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {isDateModalOpen && (
        <ShowdatesModal
          isOpen={isDateModalOpen}
          onClose={() => setIsDateModalOpen(false)}
          onDateSelect={handleDateSelect} // ✅ Pass callback
          isEditMode={true} // ✅ Edit mode for Save button
          experienceId={experienceData?.data?.experience_id}
          hostId={experienceData?.data?.host_id}
          // price={experienceData?.data?.price}
          price={experienceData?.data?.price}
          prices={{
            adult: Number(experienceData?.data?.price || 0),
            child: Number(experienceData?.data?.child_price || 0),
            infant: Number(experienceData?.data?.infant_price || 0),
          }}
          guest={experienceData?.data?.guest}
          maxLimits={{
            maxAdult: experienceData?.package?.no_of_adult,
            maxChild: experienceData?.package?.no_of_childs,
            maxInfant: experienceData?.package?.no_of_infant,
          }}
          guestType={
            experienceData?.data?.price
              ? "adult"
              : experienceData?.data?.child_price
                ? "child"
                : experienceData?.data?.infant_price
                  ? "infant"
                  : "adult"
          }
          currencyId={currencyId}
          currency_symbol={currencysymbol}
          currency={currency}
          packageId={packageId}
        />
      )}

      <GuestEditModal
        isOpen={guestModalOpen}
        onClose={() => setGuestModalOpen(false)}
        currentGuests={guests}
        onSave={handleGuestSave}
      />
      {/* <!-- End Confirm and pay --> */}
      <LoginModal
        isOpen={loginOpen}
        onClose={() => setLoginOpen(false)}
        userType="1"
      />

      {showOtherInfoModal && (
        <OtherInfo
          isOpen={showOtherInfoModal}
          onClose={closeOtherInfo}
          user={storedUser}
          userType={storedUser?.user_type}
        />
      )}

      <PaymentLoadingModal isOpen={isPaying} type="loading" />
      <CouponUpdatedModal
        isOpen={showCouponUpdatedModal}
        message={couponUpdateMessage}
        onClose={() => setShowCouponUpdatedModal(false)}
      />
    </>
  );
}

export default Checkout;
